import { Quill } from 'vue2-editor';
import { formatImageTypeSize } from './briefImageTypeUtils';

export const IMAGE_TYPE_TAG_CLASS = 'brief-image-type-tag';
export const IMAGE_TYPE_TAG_BLOT = 'imageType';

function attrOrNull(node, name) {
  const value = node.getAttribute(name);
  return value == null || value === '' ? null : value;
}

function toIntOrNull(value) {
  if (value == null || value === '') {
    return null;
  }
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function imageTypeTagLabel(payload) {
  const name = (payload && (payload.name || payload.key)) || 'Image';
  const size = formatImageTypeSize(payload && payload.width, payload && payload.height);
  return size ? `${name} · ${size}` : name;
}

export function imageTypeTagValueFromNode(node) {
  if (!node || !node.getAttribute) {
    return null;
  }
  const key = attrOrNull(node, 'data-image-type-id');
  if (!key) {
    return null;
  }
  return {
    key,
    name: attrOrNull(node, 'data-name') || (node.textContent || '').replace(/\s+·\s+\d.+$/, '').trim() || key,
    width: toIntOrNull(attrOrNull(node, 'data-width')),
    height: toIntOrNull(attrOrNull(node, 'data-height')),
    category: attrOrNull(node, 'data-category') || '',
  };
}

function applyPayloadToNode(node, raw) {
  const payload = raw && typeof raw === 'object' ? raw : { key: String(raw || ''), name: String(raw || 'Image') };
  const key = payload.key || payload.id || '';
  node.setAttribute('contenteditable', 'false');
  node.setAttribute('data-image-type-id', key);
  if (payload.name) {
    node.setAttribute('data-name', payload.name);
  }
  if (payload.width) {
    node.setAttribute('data-width', String(payload.width));
  } else {
    node.removeAttribute('data-width');
  }
  if (payload.height) {
    node.setAttribute('data-height', String(payload.height));
  } else {
    node.removeAttribute('data-height');
  }
  if (payload.category) {
    node.setAttribute('data-category', payload.category);
  } else {
    node.removeAttribute('data-category');
  }
  node.textContent = imageTypeTagLabel(payload);
}

export function registerImageTypeBlot() {
  try {
    if (!Quill || !Quill.imports || Quill.imports[`formats/${IMAGE_TYPE_TAG_BLOT}`]) {
      return;
    }
    const Embed = Quill.import('blots/embed');
    if (!Embed) {
      return;
    }
    class ImageTypeBlot extends Embed {
      static create(value) {
        const node = super.create();
        node.classList.add(IMAGE_TYPE_TAG_CLASS);
        applyPayloadToNode(node, value);
        return node;
      }

      static value(node) {
        return imageTypeTagValueFromNode(node);
      }
    }
    ImageTypeBlot.blotName = IMAGE_TYPE_TAG_BLOT;
    ImageTypeBlot.tagName = 'SPAN';
    ImageTypeBlot.className = IMAGE_TYPE_TAG_CLASS;
    Quill.register(ImageTypeBlot);
  } catch {
    // A Quill mismatch must not prevent the brief page from loading.
  }
}

registerImageTypeBlot();

/**
 * Insert image-type tags as one numbered list. A preceding list is split so a
 * new sequence (or a later single type) restarts at 1, matching the prototype.
 */
export function insertImageTypeTags(quill, payloads, fromIndex = null) {
  if (!quill || !Array.isArray(payloads) || !payloads.length) {
    return;
  }
  quill.focus();
  let index = fromIndex;
  if (index == null) {
    const range = quill.getSelection(true);
    index = range ? range.index : Math.max(0, quill.getLength() - 1);
  }

  const lineInfo = typeof quill.getLine === 'function' ? quill.getLine(index) : null;
  const line = lineInfo && lineInfo[0];
  if (line) {
    const lineStart = quill.getIndex(line);
    if (index - lineStart > 0) {
      quill.insertText(index, '\n', 'user');
      index += 1;
    }
  }

  if (index > 0) {
    const formats = quill.getFormat(Math.max(0, index - 1));
    if (formats && formats.list) {
      quill.insertText(index, '\n', {}, 'user');
      index += 1;
    }
  }

  payloads.forEach((payload) => {
    quill.insertEmbed(index, IMAGE_TYPE_TAG_BLOT, payload, 'user');
    index += 1;
    quill.insertText(index, '\n', { list: 'ordered' }, 'user');
    index += 1;
  });

  quill.setSelection(index, 0, 'user');
}
